import { useEffect, useState } from "react";
import {
  getVenues,
  getVenueCities,
  type CityCount,
  updateSensor,
  updateVenueStatus,
  sensorOnlineWindowMs,
  type VenueStatus,
  type VenueType,
} from "../../../Services/Admin/Venues";
import VenueEditor from "./VenueEditor";
import DeleteSensorButton from "../SensorsPanel/DeleteSensorButton";
import "./VenuesPanel.css";

type StatusFilter = "all" | VenueStatus;
type TypeFilter =
  | "all"
  | "bar"
  | "pub"
  | "nightclub"
  | "music_venue"
  | "restaurant"
  | "cafe"
  | "hotel"
  | "unknown";

function sensorIsOnline(sensor: VenueType["sensors"][number], venue: VenueType) {
  if (!sensor.lastSeenAt) return false;
  const freshness = sensorOnlineWindowMs(venue.sensorSettings?.intervalSeconds);
  return Date.now() - new Date(sensor.lastSeenAt).getTime() <= freshness;
}

export default function VenuesPanel() {
  const [venues, setVenues] = useState<VenueType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [total, setTotal] = useState(0);
  const [cityFilter, setCityFilter] = useState("all");
  const [cities, setCities] = useState<CityCount[]>([]);
  const [citiesError, setCitiesError] = useState<string | null>(null);
  const [withoutCity, setWithoutCity] = useState(0);
  const [offset, setOffset] = useState(0);

  const PAGE_SIZE = 100;

  useEffect(() => {
    // Felet får inte sväljas. Utan städer blir rullgardinen tyst tom, och
    // det ser ut som att filtret slutat fungera i stället för att ett
    // anrop fallerat.
    getVenueCities()
      .then((res) => {
        setCities(res.items);
        setWithoutCity(res.withoutCity ?? 0);
        setCitiesError(null);
      })
      .catch((e: any) => {
        setCities([]);
        setCitiesError(e?.message ?? "Could not load cities");
      });
  }, [reloadKey]);

  useEffect(() => {
    let alive = true;

    const t = setTimeout(() => {
      setLoading(true);
      setError(null);

      getVenues({
        q: q.trim() || undefined,
        status: statusFilter,
        venueType: typeFilter,
        city: cityFilter,
        limit: PAGE_SIZE,
        offset,
      })
        .then((page) => {
          if (!alive) return;
          setVenues(page.items);
          setTotal(page.total);
        })
        .catch((e: any) => {
          if (!alive) return;
          setError(e?.message ?? "Failed to load venues");
        })
        .finally(() => {
          if (!alive) return;
          setLoading(false);
        });
    }, 250);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, statusFilter, typeFilter, cityFilter, offset, reloadKey]);

  // Filtreringen körs i databasen. Att filtrera klientsidan hade bara
  // filtrerat den sida man råkar titta på.
  const filteredVenues = venues;

  /**
   * Sätter status direkt från raden, utan att expandera. Optimistiskt —
   * listan hoppar annars till efter varje klick, vilket gör triage av
   * tusen rader outhärdligt.
   */
  const setStatus = async (venueId: string, next: VenueStatus) => {
    const previous = venues.find((v) => v.id === venueId)?.status;

    setVenues((prev) =>
      next === "deleted"
        ? prev.filter((v) => v.id !== venueId)
        : prev.map((v) => (v.id === venueId ? { ...v, status: next } : v))
    );

    try {
      await updateVenueStatus(venueId, next);
      if (next === "deleted") setTotal((t) => Math.max(0, t - 1));
    } catch (e: any) {
      setError(e?.message ?? "Could not update the venue");
      // Rulla tillbaka genom att hämta om — raden kan ha försvunnit ur
      // listan och går inte att sätta tillbaka lokalt.
      if (next === "deleted" || !previous) setReloadKey((k) => k + 1);
      else setVenues((prev) => prev.map((v) => (v.id === venueId ? { ...v, status: previous } : v)));
    }
  };

  /**
   * Tar bort sensorraden ur listan. Sensorn kan ligga under vilket venue
   * som helst av de laddade, så alla gås igenom: efter en frånkoppling
   * finns den inte längre under något av dem.
   */
  const removeSensorFromList = (sensorId: string) =>
    setVenues((prev) =>
      prev.map((venue) => ({
        ...venue,
        sensors: venue.sensors.filter((sensor) => sensor.sensorId !== sensorId),
      }))
    );

  /**
   * Frånkoppling som en egen knapp.
   *
   * Det gick förut bara via "Unassigned" i rullgardinen plus Save, och
   * det är inte något någon hittar. Sensorn hamnar under "Oanslutna
   * sensorer" och går att koppla vidare därifrån.
   */
  const onDisconnectSensor = async (sensorId: string) => {
    setError(null);

    try {
      await updateSensor(sensorId, { venueId: null });
      removeSensorFromList(sensorId);
    } catch (e: any) {
      setError(e?.message ?? "Failed to disconnect sensor");
    }
  };

  const onUpdateVenueStatus = async (
    venueId: string,
    nextStatus: VenueStatus
  ) => {
    const previous = venues.find((v) => v.id === venueId);
    if (!previous) return;

    setVenues((prev) =>
      prev.map((venue) =>
        venue.id === venueId ? { ...venue, status: nextStatus } : venue
      )
    );

    try {
      await updateVenueStatus(venueId, nextStatus);
    } catch (e: any) {
      setVenues((prev) =>
        prev.map((venue) =>
          venue.id === venueId ? { ...venue, status: previous.status } : venue
        )
      );

      setError(e?.message ?? "Failed to update venue status");
    }
  };

  const onSaveSensor = async (
  currentVenueId: string,
  sensorId: string,
  data: {
    venueId: string | null;
    isActive: boolean;
  }
) => {
  try {
    const result: any = await updateSensor(sensorId, data);
    const updatedSensor = result.sensor;

    setVenues((prev) =>
      prev.map((venue) => ({
        ...venue,
        sensors: venue.sensors
          .filter((sensor) => sensor.sensorId !== sensorId)
          .concat(
            venue.id === updatedSensor.venueId
              ? [
                  {
                    ...updatedSensor,
                    lastSeenAt:
                      updatedSensor.lastSeenAt?.toString?.() ??
                      updatedSensor.lastSeenAt,
                  },
                ]
              : []
          ),
      }))
    );
  } catch (e: any) {
    setError(e?.message ?? "Failed to update sensor");
  }
};


  return (
    <div className="admin-panel">
      <div className="admin-toolbar">
        <input
          className="admin-toolbar-grow"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search venue / id / address / timezone…"
        />

        <select
          value={statusFilter}
          onChange={(e) => { setOffset(0); setStatusFilter(e.target.value as StatusFilter); }}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="needs_review">Needs review</option>
          <option value="hidden">Hidden</option>
          <option value="deleted">Deleted</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => { setOffset(0); setTypeFilter(e.target.value as TypeFilter); }}
        >
          <option value="all">All types</option>
          <option value="bar">Bar</option>
          <option value="pub">Pub</option>
          <option value="nightclub">Nightclub</option>
          <option value="music_venue">Music venue</option>
          <option value="restaurant">Restaurant</option>
          <option value="cafe">Café</option>
          <option value="hotel">Hotel</option>
          <option value="unknown">Unknown / manual</option>
        </select>

        <select
          value={cityFilter}
          onChange={(e) => {
            setOffset(0);
            setCityFilter(e.target.value);
          }}
        >
          <option value="all">{citiesError ? "Cities unavailable" : "All cities"}</option>
          {cities.map((c) => (
            <option key={c.city} value={c.city}>
              {c.city} ({c.count})
            </option>
          ))}
          {withoutCity > 0 && (
            <option value="unknown">No city ({withoutCity})</option>
          )}
        </select>
      </div>

      {withoutCity > 0 && (
        <div className="admin-notice">
          {withoutCity} venues have no city and cannot be filtered by city.
          Run <code>node scripts/backfillVenueCity.js</code> on the server —
          without <code>--dry</code>.
        </div>
      )}

      {citiesError && (
        <div className="admin-error">City filter unavailable: {citiesError}</div>
      )}

      {loading && <p className="admin-hint">Loading…</p>}
      {error && <div className="admin-error">{error}</div>}

      <p className="admin-hint">
        Showing {total === 0 ? 0 : offset + 1}–{offset + filteredVenues.length} of {total}
      </p>

      {!loading && filteredVenues.length === 0 && (
        <p className="admin-empty">No venues match these filters.</p>
      )}

      <div className="venue-list">
       {filteredVenues.map((v) => (
  <details className="venue-card" key={v.id}>
    <summary className="venue-summary">
      <div>
        <strong>{v.name}</strong>
        <span>{[v.city, v.address].filter(Boolean).join(" · ") || "No city or address"}</span>
      </div>

      <div className="venue-summary-meta">
        {/* Etiketterna hålls i en egen låda, och typ och sensorantal får
            fast bredd. Annars hamnar knapparna på olika ställe i varje rad
            och listan går inte att skumma. */}
        <span className="venue-pills">
          <span className={`venue-pill ${v.status}`}>{v.status}</span>
          {v.plan === "premium" && <span className="venue-pill premium">premium</span>}
          {!v.openHoursJson && <span className="venue-pill missing">no hours</span>}
          {v.plan === "premium" && v.sensors.length === 0 && <span className="venue-pill missing">sensor needed</span>}
          {v.sensors.length > 0 && v.sensors.some((sensor) => !sensorIsOnline(sensor, v)) && <span className="venue-pill missing">sensor offline</span>}
        </span>

        <span className="venue-meta-cell">{v.venueType ?? "unknown"}</span>
        <span className="venue-meta-cell">
          {v.sensors.length === 0
            ? "no sensor"
            : `${v.sensors.filter((sensor) => sensorIsOnline(sensor, v)).length}/${v.sensors.length} online`}
        </span>

        {/* Stoppar summary från att fälla ut kortet när man klickar en knapp. */}
        <span
          className="venue-row-actions"
          onClick={(e) => e.preventDefault()}
        >
          {v.status !== "active" && (
            <button
              type="button"
              className="venue-row-button activate"
              onClick={() => setStatus(v.id, "active")}
            >
              Activate
            </button>
          )}

          {v.status !== "hidden" && (
            <button
              type="button"
              className="venue-row-button"
              onClick={() => setStatus(v.id, "hidden")}
            >
              Hide
            </button>
          )}

          <button
            type="button"
            className="venue-row-button danger"
            onClick={() => {
              const ok = window.confirm(
                `Delete ${v.name}?\n\nIt stays in the database as deleted so a future ` +
                  `import cannot bring it back, and disappears from the map and this list.`
              );
              if (ok) void setStatus(v.id, "deleted");
            }}
          >
            Delete
          </button>
        </span>
      </div>
    </summary>

    <div className="venue-body">
      <div className="venue-edit-toggle">
        <button
          type="button"
          onClick={() => setEditingId(editingId === v.id ? null : v.id)}
        >
          {editingId === v.id ? "Close editor" : "Edit venue"}
        </button>
      </div>

      {editingId === v.id && (
        <VenueEditor
          venue={v}
          knownCities={cities.map((c) => c.city)}
          onClose={() => setEditingId(null)}
          onSaved={(updated) =>
            setVenues((prev) =>
              prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x))
            )
          }
        />
      )}

      <div className="venue-grid">
        <div>
          <span className="admin-label">ID</span>
          <p>{v.id}</p>
        </div>

        <div>
          <span className="admin-label">Source</span>
          <p>{v.source ?? "manual"}</p>
        </div>

        <div>
          <span className="admin-label">Coordinates</span>
          <p>
            {v.lat ?? "—"}, {v.lon ?? "—"}
          </p>
        </div>
      </div>

      <div className="venue-actions">
        <button
          type="button"
          disabled={v.status === "active"}
          onClick={() => onUpdateVenueStatus(v.id, "active")}
        >
          Activate
        </button>

        <button
          type="button"
          disabled={v.status === "needs_review"}
          onClick={() => onUpdateVenueStatus(v.id, "needs_review")}
        >
          Review
        </button>

        <button
          type="button"
          disabled={v.status === "hidden"}
          onClick={() => onUpdateVenueStatus(v.id, "hidden")}
        >
          Hide
        </button>
      </div>

      <div className="sensor-section">
        <h4>Sensors</h4>

        {v.sensors.length > 0 && (
          <p className="admin-hint">
            Recognition every {v.sensorSettings.intervalSeconds} s,{" "}
            {v.sensorSettings.recordSeconds} s clips · activity{" "}
            {v.sensorSettings.activity.enabled
              ? `every ${v.sensorSettings.activity.intervalSeconds} s`
              : "off"}
            . Change under Edit.
          </p>
        )}

        {v.sensors.length === 0 ? (
          <p className="admin-hint">No sensor connected</p>
        ) : (
          v.sensors.map((s) => (
            <form
              key={s.sensorId}
              className="sensor-card"
              onSubmit={(e) => {
                e.preventDefault();

                const form = new FormData(e.currentTarget);

                onSaveSensor(v.id, s.sensorId, {
                  venueId: String(form.get("venueId") || "") || null,
                  isActive: form.get("isActive") === "on",
                });
              }}
            >
              <div className="sensor-head">
                <strong>{s.sensorId}</strong>
                <span>{s.isActive ? "ACTIVE" : "INACTIVE"}</span>
              </div>

              <div className="sensor-grid">
                <label>
                  Venue
                  <select name="venueId" defaultValue={s.venueId ?? v.id}>
                    <option value="">Unassigned</option>
                    {venues.map((venue) => (
                      <option key={venue.id} value={venue.id}>
                        {venue.name}
                      </option>
                    ))}
                  </select>
                </label>


                <label className="sensor-checkbox">
                  <input
                    name="isActive"
                    type="checkbox"
                    defaultChecked={s.isActive}
                  />
                  Active
                </label>
              </div>

              <div className="sensor-footer">
                <span>Last seen: {s.lastSeenAt ?? "—"}</span>
                <span>Last status: {s.lastStatus ?? "—"}</span>
                <button type="submit">Save sensor</button>
              </div>

              <div className="sensor-danger">
                <button
                  type="button"
                  className="admin-btn--danger"
                  onClick={() => onDisconnectSensor(s.sensorId)}
                >
                  Disconnect from venue
                </button>

                <DeleteSensorButton
                  sensorId={s.sensorId}
                  name={s.installationName ?? s.name}
                  onDeleted={({ sensorId }) => removeSensorFromList(sensorId)}
                  onError={setError}
                />
              </div>
            </form>
          ))
        )}
      </div>
    </div>
  </details>
))}

        {total > PAGE_SIZE && (
          <div className="venue-pager">
            <button
              type="button"
                  disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ‹ Previous
            </button>

            <span className="venue-pager-page">
              Page {Math.floor(offset / PAGE_SIZE) + 1} of{" "}
              {Math.max(1, Math.ceil(total / PAGE_SIZE))}
            </span>

            <button
              type="button"
                  disabled={offset + PAGE_SIZE >= total || loading}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
